import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faHandshake, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const DonationSection = styled.section`
  padding: clamp(3rem, 8vw, 4rem) 0;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s ease-out;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 5vw, 2rem);
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(2rem, 6vw, 3rem);
  padding: 0 1rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease-out;
  transition-delay: 0.2s;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SubTitle = styled.h3`
  font-size: clamp(1.5rem, 4vw, 2rem);
  margin-bottom: 0.5rem;
  text-align: center;
  line-height: 1.3;
`;

const BeatSpan = styled.span`
  display: inline-block;
  color: ${props => props.theme.colors.primary};
  animation: heartBeat 1.2s ease-in-out infinite;
  transform-origin: center;

  @keyframes heartBeat {
    0% {
      transform: scale(1);
    }
    14% {
      transform: scale(1.2);
    }
    28% {
      transform: scale(1);
    }
    42% {
      transform: scale(1.2);
    }
    70% {
      transform: scale(1);
    }
  }
`;

const Title = styled.h2`
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
  text-align: center;
  line-height: 1.3;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
  
  .highlight { 
    padding-right: 0.5rem;
    color: #B5873A;
  }
  
  @media (max-width: 480px) {
    br {
      display: none;
    }
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(1.5rem, 4vw, 2rem);
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  padding: clamp(1.5rem, 4vw, 2rem);
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.5s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(30px);
  min-height: 500px;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
    background: linear-gradient(145deg, ${props => props.theme.colors.light}; 0%, #f8f9fa 100%);
  }
  
  &:nth-child(1) { transition-delay: 0.2s; }
  &:nth-child(2) { transition-delay: 0.2s; }
  &:nth-child(3) { transition-delay: 0.2s; }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    min-height: auto;
  }
`;

const IconWrapper = styled.div`
  width: clamp(60px, 15vw, 80px);
  height: clamp(60px, 15vw, 80px);
  background: ${props => props.bgColor || '#800000'};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto clamp(1rem, 3vw, 1.5rem);
  font-size: clamp(1.5rem, 4vw, 2rem);
  transition: transform 0.3s ease;
  
  ${Card}:hover & {
    transform: scale(1.1);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
  display: flex;
  align-items: center;
`;

const CardButton = styled.button`
  background: ${props => props.bgColor || '#800000'};
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  width: 100%;
  max-width: 200px;
  margin-top: auto;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: ${props => props.hoverColor || '#600000'};
    transition: width 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    
    &:before {
      width: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

function JoinUs() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);
  return (
    <DonationSection ref={sectionRef} className={isVisible ? 'visible' : ''}>
      <Container>
        <SectionHeader className={isVisible ? 'visible' : ''}>
          <SubTitle>Join Us</SubTitle>
          <Title>
            <span className="highlight">Let's Empower the Heart</span> <BeatSpan> Beat</BeatSpan>
          </Title>
        </SectionHeader>

        <CardsContainer>
          <Card className={isVisible ? 'visible' : ''}>
            <IconWrapper bgColor="#800000">
              <FontAwesomeIcon icon={faUsers} />
            </IconWrapper>
            <CardTitle>Become a Member</CardTitle>
            <CardText>
              Join our community of dedicated members and support impactful initiatives across Morocco. As a member, you contribute to shaping our projects and help sustain our efforts in education, healthcare, environmental protection, and more.Together, we can make a lasting impact and foster positive change. Sign up today and be part of the change.
            </CardText>
            <Link to="/join">
              <CardButton bgColor="#800000" hoverColor="#600000">
                Sign up now
              </CardButton>
            </Link>
          </Card>

          <Card className={isVisible ? 'visible' : ''}>
            <IconWrapper bgColor="#B5873A">
              <FontAwesomeIcon icon={faHandshake} />
            </IconWrapper>
            <CardTitle>Sponsorship Opportunities</CardTitle>
            <CardText>
              Partner with us to create a lasting impact in Morocco. Your sponsorship helps fund vital projects, from renovating schools to providing essential resources for underprivileged communities. Together, we can empower lives and foster development.
            </CardText>
            <Link to="/contact">
              <CardButton bgColor="#B5873A" hoverColor="#9E7431">
                Contact us
              </CardButton>
            </Link>
          </Card>

          <Card className={isVisible ? 'visible' : ''}>
            <IconWrapper bgColor="#800000">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </IconWrapper>
            <CardTitle>Participate & Support</CardTitle>
            <CardText>
              Join our events and activities designed to support our mission through ticket-based participation. Whether it’s a joyful gathering, a cultural evening, a workshop, or a training session — each event helps fund our projects and bring positive change to communities. By attending, you’re not just enjoying the moment you’re helping us make a real impact.
            </CardText>
            <Link to="/events">
              <CardButton bgColor="#800000" hoverColor="#600000">
                Attend an Event
              </CardButton>
            </Link>
          </Card>
        </CardsContainer>
      </Container>
    </DonationSection>
  );
}

export default JoinUs;
